import React from "react";
import { NotePencil, ChatCircleDots, Lightning, CheckCircle, UsersThree, Stack, Brain, Sparkle } from "@phosphor-icons/react";

const fmt = (n) => (n ?? 0).toLocaleString();

function Metric({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5" data-testid={`admin-metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${color}1a`, color }}><Icon size={18} weight="duotone" /></span>
      <p className="mt-3 font-heading text-3xl font-semibold text-slate-100">{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-slate-500">{label}</p>
    </div>
  );
}

export default function AdminEngagement({ data }) {
  const e = data.engagement;
  const ai = data.ai;
  const total = ai.narratives_total || 1;
  const readyPct = Math.round((ai.narratives_ready / total) * 100);

  return (
    <div data-testid="admin-engagement">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Engagement & AI</p>
      <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50">Activity & AI usage</h1>

      <h2 className="mt-8 font-heading text-lg font-medium text-slate-200">Engagement</h2>
      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <Metric icon={UsersThree} label="Active Users" value={fmt(e.active_users)} color="#38BDF8" />
        <Metric icon={Stack} label="Avg Assessments / User" value={e.avg_assessments_per_user} color="#14B8A6" />
        <Metric icon={NotePencil} label="Journal Entries" value={fmt(e.journal_entries)} color="#A78BFA" />
        <Metric icon={ChatCircleDots} label="Coach Messages" value={fmt(e.coach_messages)} color="#22D3EE" />
        <Metric icon={Lightning} label="Challenges Joined" value={fmt(e.challenges_joined)} color="#FBBF24" />
        <Metric icon={CheckCircle} label="Challenges Completed" value={fmt(e.challenges_completed)} color="#34D399" />
      </div>

      <h2 className="mt-10 font-heading text-lg font-medium text-slate-200">AI analytics</h2>
      <div className="mt-4 grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Metric icon={Brain} label="Narratives Generated" value={fmt(ai.narratives_total)} color="#F59E0B" />
          <Metric icon={ChatCircleDots} label="Coach Messages" value={fmt(ai.coach_messages)} color="#22D3EE" />
          <Metric icon={Sparkle} label="Avg Coach / Active User" value={ai.avg_coach_msgs_per_active_user} color="#A78BFA" />
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid="admin-ai-status">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">AI report pipeline</p>
          <div className="mt-3 flex items-center gap-2">
            <span className="rounded-md bg-slate-800 px-2 py-1 font-mono text-xs text-teal-300">{ai.model}</span>
          </div>
          <div className="mt-5 space-y-3 text-sm">
            <Row label="Ready" value={ai.narratives_ready} color="#34D399" />
            <Row label="Pending" value={ai.narratives_pending} color="#FBBF24" />
            <Row label="Failed" value={ai.narratives_failed} color="#FB7185" />
          </div>
          <div className="mt-5">
            <div className="flex justify-between text-xs text-slate-500"><span>Delivery success</span><span>{readyPct}%</span></div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full bg-emerald-400" style={{ width: `${readyPct}%` }} /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-slate-400"><span className="h-2 w-2 rounded-full" style={{ background: color }} /> {label}</span>
      <span className="font-mono text-slate-200">{fmt(value)}</span>
    </div>
  );
}
